import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useState, useEffect, useRef } from "react";
import {
  House,
  Buildings,
  Bed,
  CalendarBlank,
  Users,
  Broadcast,
  Gear,
  SignOut,
  Sun,
  MoonStars,
  X,
  Receipt,
  PaintBrushBroad,
  ChartBar,
  FileText,
  IdentificationCard,
  CurrencyEur,
  ChatCircleDots,
  PaperPlaneTilt,
  Bell,
  ArrowsClockwise,
  CheckCircle,
  Warning,
  List as ListIcon,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { useNotificationFeed, LiveToasts, NotificationDrawer } from "@/components/NotificationCenter";
import { useSwipeDismiss } from "@/components/motion/useSwipeDismiss";
import { syncPush } from "@/lib/push";
import PropertySwitcher from "@/components/PropertySwitcher";
import EnvBadge from "@/components/EnvBadge";

// Grouped navigation — labels always visible in the expanded sidebar
const NAV_PRIMARY = [
  { to: "/dashboard", label: "Kokpit", icon: House, testId: "nav-dashboard" },
  { to: "/calendar", label: "Kalendar", icon: CalendarBlank, testId: "nav-calendar" },
  { to: "/reservations", label: "Rezervacije", icon: Receipt, testId: "nav-reservations" },
  { to: "/invoices", label: "Računi", icon: FileText, testId: "nav-invoices-list" },
  { to: "/inbox", label: "Inbox", icon: ChatCircleDots, testId: "nav-inbox", badgeKey: "inbox" },
  { to: "/guests", label: "Gosti/Tvrtke", icon: Users, testId: "nav-guests" },
];

const NAV_SECONDARY = [
  { to: "/properties", label: "Smještaji", icon: Buildings, testId: "nav-properties" },
  { to: "/room-types", label: "Tipovi smještaja", icon: Bed, testId: "nav-room-types" },
  { to: "/units", label: "Jedinice", icon: Bed, testId: "nav-units" },
  { to: "/pricing", label: "Cijene", icon: CurrencyEur, testId: "nav-pricing" },
  { to: "/check-in", label: "eVisitor", icon: IdentificationCard, testId: "nav-checkin" },
  { to: "/channels", label: "Kanali", icon: Broadcast, testId: "nav-channels" },
  { to: "/settings/templates", label: "Auto poruke", icon: PaperPlaneTilt, testId: "nav-templates" },
];

const NAV_BOTTOM = [
  { to: "/statistics", label: "Statistike", icon: ChartBar, testId: "nav-statistics" },
  { to: "/settings/invoicing", label: "Fakturiranje", icon: Receipt, testId: "nav-invoicing" },
  { to: "/theme", label: "Tema", icon: PaintBrushBroad, testId: "nav-theme" },
  { to: "/settings", label: "Postavke", icon: Gear, testId: "nav-settings" },
];

const ALL_NAV = [...NAV_PRIMARY, ...NAV_SECONDARY, ...NAV_BOTTOM];

function SidebarLink({ item, badge = 0 }) {
  const disabled = !!item.disabled;

  const inner = (
    <>
      <item.icon size={18} weight="regular" className="shrink-0" />
      <span className="flex-1 truncate">{item.label}</span>
      {disabled && (
        <span className="rounded bg-white/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-white/50">Uskoro</span>
      )}
      {badge > 0 && (
        <span
          className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white"
          data-testid={`${item.testId}-badge`}
        >
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </>
  );

  if (disabled) {
    return (
      <div
        className="flex cursor-not-allowed items-center gap-3 rounded-md px-3 py-2 text-sm text-sidebar-muted opacity-50"
        data-testid={item.testId}
        title="Uskoro dostupno"
      >
        {inner}
      </div>
    );
  }

  return (
    <NavLink
      to={item.to}
      end={item.to === "/settings"}
      className={({ isActive }) =>
        `group relative flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
          isActive
            ? "bg-white/10 text-sidebar-active font-semibold"
            : "text-sidebar-muted hover:bg-white/5 hover:text-sidebar-foreground"
        }`
      }
      data-testid={item.testId}
    >
      {({ isActive }) => (
        <>
          {isActive && <span className="absolute left-0 top-1/2 h-6 w-0.5 -translate-y-1/2 rounded-r bg-sidebar-active" />}
          {inner}
        </>
      )}
    </NavLink>
  );
}

function SidebarGroupLabel({ children }) {
  return (
    <p className="px-3 pt-4 pb-1 text-[10px] font-bold uppercase tracking-[0.16em] text-white/40">{children}</p>
  );
}

function DesktopSidebar({ tenant, user, badges }) {
  return (
    <aside
      className="fixed inset-y-0 left-0 z-40 hidden w-56 flex-col border-r border-white/5 bg-sidebar lg:flex"
      data-testid="app-sidebar"
    >
      {/* Brand */}
      <div className="flex items-center gap-3 border-b border-white/5 px-4 py-4">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground shadow-sm">
          {tenant?.logo_url ? (
            <img src={tenant.logo_url} alt="" className="h-6 w-6 rounded object-contain" />
          ) : (
            <span className="text-sm font-bold">{(tenant?.brand_name?.[0] || "L").toUpperCase()}</span>
          )}
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-sidebar-foreground">{tenant?.brand_name || "Lodgy.co"}</p>
          <p className="truncate text-[11px] text-sidebar-muted">{tenant?.slug || "tenant"}</p>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 space-y-0.5 rw-nav-scroll overflow-y-auto px-2 py-2">
        <SidebarGroupLabel>Glavno</SidebarGroupLabel>
        {NAV_PRIMARY.map((item) => (
          <SidebarLink key={item.testId} item={item} badge={item.badgeKey ? badges[item.badgeKey] : 0} />
        ))}
        <SidebarGroupLabel>Podaci</SidebarGroupLabel>
        {NAV_SECONDARY.map((item) => (
          <SidebarLink key={item.testId} item={item} />
        ))}
        <SidebarGroupLabel>Postavke</SidebarGroupLabel>
        {NAV_BOTTOM.map((item) => (
          <SidebarLink key={item.testId} item={item} />
        ))}
      </nav>

      {/* User footer */}
      <div className="border-t border-white/5 px-3 py-3">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-xs font-semibold text-sidebar-foreground">
            {(user?.name?.[0] || user?.email?.[0] || "U").toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-medium text-sidebar-foreground">{user?.name || "Admin"}</p>
            <p className="truncate text-[11px] text-sidebar-muted">{user?.email}</p>
          </div>
        </div>
      </div>
    </aside>
  );
}

function MobileMenu({ open, onClose, tenant, user, badges, onLogout }) {
  const backdropRef = useRef(null);
  const panelRef = useSwipeDismiss({ onClose, directions: ["left"], enabled: open, backdropRef });
  if (!open) return null;
  return (
    <>
      <div ref={backdropRef} onClick={onClose} className="fixed inset-0 z-40 bg-black/50 lg:hidden" data-testid="mobile-sidebar-overlay" />
      <aside ref={panelRef} className="rw-slide-left fixed inset-y-0 left-0 z-50 flex w-72 flex-col bg-sidebar text-sidebar-foreground lg:hidden" data-testid="mobile-sidebar">
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground font-bold">
              {(tenant?.brand_name?.[0] || "L").toUpperCase()}
            </div>
            <div>
              <p className="text-sm font-semibold">{tenant?.brand_name || "Lodgy.co"}</p>
              <p className="text-xs text-sidebar-muted">{tenant?.slug || "tenant"}</p>
            </div>
          </div>
          <button onClick={onClose} className="rounded p-1 text-sidebar-muted hover:bg-white/10" data-testid="mobile-sidebar-close">
            <X size={18} />
          </button>
        </div>
        <nav className="flex-1 space-y-0.5 rw-nav-scroll overflow-y-auto p-2">
          {ALL_NAV.map((item) => (
            <div key={item.testId} onClick={onClose}>
              <SidebarLink item={item} badge={item.badgeKey ? badges[item.badgeKey] : 0} />
            </div>
          ))}
        </nav>
        <div className="flex items-center gap-3 border-t border-white/10 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/10 text-xs font-semibold">
            {(user?.name?.[0] || user?.email?.[0] || "U").toUpperCase()}
          </div>
          <p className="min-w-0 flex-1 truncate text-xs text-sidebar-muted">{user?.email}</p>
          <button onClick={onLogout} className="flex shrink-0 items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-sidebar-muted hover:bg-white/10 hover:text-sidebar-foreground" data-testid="mobile-logout-btn">
            <SignOut size={15} /> Odjava
          </button>
        </div>
      </aside>
    </>
  );
}

const MOBILE_TABS = [
  NAV_PRIMARY[0], NAV_PRIMARY[1], NAV_PRIMARY[2], NAV_PRIMARY[4],
];

function MobileBottomNav({ badges, onMore }) {
  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-30 flex h-16 items-stretch border-t border-border bg-card/95 pb-[env(safe-area-inset-bottom)] backdrop-blur-lg lg:hidden"
      data-testid="mobile-bottom-nav"
    >
      {MOBILE_TABS.map((item) => (
        <NavLink
          key={item.testId}
          to={item.to}
          className={({ isActive }) =>
            `relative flex flex-1 flex-col items-center justify-center gap-0.5 text-[10px] font-medium transition-colors ${
              isActive ? "text-primary" : "text-muted-foreground"
            }`
          }
          data-testid={`mobile-tab-${item.testId}`}
        >
          {({ isActive }) => (
            <>
              {isActive && <span className="absolute top-0 h-0.5 w-8 rounded-b bg-primary" />}
              <span className="relative">
                <item.icon size={22} weight={isActive ? "fill" : "regular"} />
                {item.badgeKey && badges[item.badgeKey] > 0 && (
                  <span className="absolute -right-2 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-rose-500 px-1 text-[9px] font-bold text-white">
                    {badges[item.badgeKey] > 99 ? "99+" : badges[item.badgeKey]}
                  </span>
                )}
              </span>
              {item.label}
            </>
          )}
        </NavLink>
      ))}
      <button
        onClick={onMore}
        className="flex flex-1 flex-col items-center justify-center gap-0.5 text-[10px] font-medium text-muted-foreground"
        data-testid="mobile-tab-more"
      >
        <ListIcon size={22} />
        Više
      </button>
    </nav>
  );
}

/**
 * Sync status indicator — polls /api/channels/channex/activity every 15s.
 * Shows queue length + last completed sync so user can SEE their PMS changes propagating to Channex.
 */
function SyncStatusPill() {
  const [state, setState] = useState({ pending: 0, lastSuccess: null, lastFailed: null, loading: true });
  const [syncing, setSyncing] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/channels/channex/activity");
      const activePending = (data.pending || []).filter((t) => t.status === "pending" || t.status === "running");
      const lastLog = (data.log || [])[0];
      const lastSuccess = (data.log || []).find((l) => l.status === "success");
      const lastFailed = (data.log || []).find((l) => l.status === "failed");
      // If most recent event is a failure -> highlight red
      const recentFailure = lastLog && lastLog.status === "failed";
      setState({
        pending: activePending.length,
        lastSuccess: lastSuccess?.completed_at,
        lastFailed: recentFailure ? lastLog.completed_at : (lastFailed?.completed_at || null),
        recentFailure,
        loading: false,
      });
    } catch {
      setState((s) => ({ ...s, loading: false }));
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, []);

  const { pending, lastSuccess, recentFailure } = state;

  // Determine visual state
  let color = "text-emerald-600 bg-emerald-50 border-emerald-200";
  let Icon = CheckCircle;
  let label = "Sync aktivan";
  if (pending > 0 || syncing) {
    color = "text-blue-600 bg-blue-50 border-blue-200";
    Icon = ArrowsClockwise;
    label = `${pending} u redu`;
  } else if (recentFailure) {
    color = "text-rose-600 bg-rose-50 border-rose-200";
    Icon = Warning;
    label = "Sync greška";
  } else if (!lastSuccess) {
    color = "text-slate-600 bg-slate-50 border-slate-200";
    Icon = ArrowsClockwise;
    label = "Sync";
  }

  const formatWhen = (iso) => {
    if (!iso) return "još nikad";
    try {
      const d = new Date(iso);
      const diffMin = Math.floor((Date.now() - d.getTime()) / 60000);
      if (diffMin < 1) return "prije trenutak";
      if (diffMin < 60) return `prije ${diffMin} min`;
      const h = Math.floor(diffMin / 60);
      if (h < 24) return `prije ${h} h`;
      return d.toLocaleDateString("hr-HR");
    } catch { return iso; }
  };

  return (
    <button
      onClick={() => navigate("/channels")}
      title={`Sinkronizacija — Zadnja uspješna: ${formatWhen(lastSuccess)}${recentFailure ? ` • Greška: ${formatWhen(state.lastFailed)}` : ""}`}
      className={`hidden items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors md:inline-flex ${color}`}
      data-testid="sync-status-pill"
    >
      <Icon size={14} weight="bold" className={pending > 0 || syncing ? "animate-spin" : ""} />
      <span>{label}</span>
      {lastSuccess && !recentFailure && pending === 0 && (
        <span className="hidden text-[10px] font-normal opacity-80 lg:inline">• {formatWhen(lastSuccess)}</span>
      )}
    </button>
  );
}

function TopBar({ tenant, user, logout, onMenu }) {
  const { theme, toggle } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [notifOpen, setNotifOpen] = useState(false);
  const feed = useNotificationFeed();

  const active = ALL_NAV.find((n) => location.pathname === n.to || location.pathname.startsWith(n.to + "/"));
  const title = active?.label || tenant?.brand_name || "Lodgy.co";

  return (
    <header className="sticky top-0 z-40 rw-glass" data-testid="app-header">
      <div className="flex h-16 items-center justify-between gap-2 px-3 sm:gap-4 sm:px-8">
        <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
          <button onClick={onMenu} className="shrink-0 rounded-md p-2 text-muted-foreground hover:bg-muted lg:hidden" data-testid="open-sidebar-btn">
            <ListIcon size={20} />
          </button>
          <h1 className="truncate font-heading text-base font-bold tracking-tight text-foreground sm:text-xl" data-testid="page-title">{title}</h1>
          <EnvBadge />
          <PropertySwitcher />
        </div>

        <div className="flex shrink-0 items-center gap-0.5 sm:gap-1.5">
          <button
            onClick={toggle}
            className="rounded-md p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            data-testid="theme-toggle-btn"
            aria-label="Toggle theme"
            title={theme === "dark" ? "Svijetla tema" : "Tamna tema"}
          >
            {theme === "dark" ? <Sun size={18} /> : <MoonStars size={18} />}
          </button>
          <button
            onClick={() => { setNotifOpen(true); feed.setPulse(false); }}
            className={`relative rounded-md p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground ${feed.pulse ? "rw-bell-pulse text-primary" : ""}`}
            data-testid="notifications-btn"
            title="Obavijesti"
            aria-label="Notifications"
          >
            <Bell size={18} />
            {feed.unread > 0 && (
              <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-rose-500 px-1 text-[9px] font-bold text-white ring-2 ring-background" data-testid="notifications-dot">{feed.unread > 99 ? "99+" : feed.unread}</span>
            )}
          </button>
          <div className="mx-1 hidden h-5 w-px bg-border sm:block" />
          <button
            onClick={async () => { await logout(); navigate("/login"); }}
            className="hidden items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground sm:flex"
            data-testid="logout-btn"
          >
            <SignOut size={16} />
            <span className="hidden sm:inline">Odjava</span>
          </button>
        </div>
      </div>
      <NotificationDrawer open={notifOpen} onClose={() => { setNotifOpen(false); feed.refresh(); }} onRead={feed.refresh} />
      <LiveToasts toasts={feed.toasts} dismiss={feed.dismiss} hidden={notifOpen} />
    </header>
  );
}

export default function AppLayout() {
  const { user, tenant, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [badges, setBadges] = useState({ inbox: 0 });
  const { pathname } = useLocation();
  const fullBleed = pathname.startsWith("/calendar") || pathname.startsWith("/inbox");
  useEffect(() => { const t = setTimeout(syncPush, 3000); return () => clearTimeout(t); }, []);


  useEffect(() => {
    let active = true;
    const load = () => {
      api.get("/inbox/unread-count")
        .then((r) => { if (active) setBadges({ inbox: r.data?.unread || 0 }); })
        .catch(() => {});
    };
    load();
    const id = setInterval(load, 30000);
    return () => { active = false; clearInterval(id); };
  }, []);

  return (
    <div className="min-h-screen w-full bg-background">
      <DesktopSidebar tenant={tenant} user={user} badges={badges} />
      <MobileMenu open={mobileOpen} onClose={() => setMobileOpen(false)} tenant={tenant} user={user} badges={badges} onLogout={async () => { await logout(); window.location.assign("/login"); }} />
      <div className="flex min-w-0 flex-col lg:pl-56">
        <TopBar tenant={tenant} user={user} logout={logout} onMenu={() => setMobileOpen(true)} />
        <main className="min-w-0 flex-1 overflow-x-clip p-4 pb-24 sm:p-6 sm:pb-24 lg:p-8 [&:has([data-testid=calendar-page])]:p-2 [&:has([data-testid=calendar-page])]:pb-20 [&:has([data-testid=calendar-page])]:lg:p-3 [&:has([data-testid=inbox-page])]:!p-0" data-testid="app-main">
          {fullBleed ? <Outlet /> : <div key={pathname} className="rw-page mx-auto w-full max-w-[1400px]"><Outlet /></div>}
        </main>
      </div>
      <MobileBottomNav badges={badges} onMore={() => setMobileOpen(true)} />
    </div>
  );
}
